import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Clock, Loader2, MoreHorizontal, ShieldCheck, Smartphone } from "lucide-react";
import { WhatsAppIcon } from "./BrandIcons";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edgeError";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  launchWhatsAppEmbeddedSignup,
  embeddedSignupConfigured,
  metaWhatsAppEnabled,
} from "@/lib/metaEmbeddedSignup";
import {
  hasLegacyWhatsAppConnection,
  hasMetaWhatsAppCredentials,
  metaWhatsAppState,
} from "@/lib/whatsappConnection";

// WhatsApp Connection Card. Meta Cloud only for all new connections.
//
// Meta Embedded Signup (Coexistence) is available whenever the Meta app and
// configuration IDs are present. VITE_WHATSAPP_META_ENABLED=false remains an
// emergency kill switch, but it never reveals an alternative provider.
//
//  1. Each business connects through Meta. Meta-whatsapp-onboard
//     sets whatsapp_provider='meta_cloud' + meta creds, and the send
//     connector auto-routes to Meta on the next message. No backend
//     change, no redeploy of the send path.
//  2. A historical non-Meta connection may keep routing server-side during
//     migration, but the dashboard prompts the business to reconnect via Meta.
//  3. Stored credentials are NOT the same as a working number. Meta only
//     delivers inbound once the number is live, which for Coexistence means
//     the owner linked their WhatsApp Business app during signup. 'Connected'
//     here means whatsapp_status='connected', which the backend writes only
//     when Meta itself reports status=CONNECTED. Anything else shows as
//     unfinished setup, and the remedy is to reconnect through Meta. Never to
//     register the number, which would take WhatsApp off the owner's phone.

interface BusinessWhatsAppRow {
  business_id: string;
  whatsapp_provider: string | null;
  whatsapp_number: string | null;
  whatsapp_status: string | null;
  meta_phone_number_id: string | null;
  meta_connected_at: string | null;
}

const WhatsAppConnectionCard = ({ className }: { className?: string }) => {
  const { user, businessId } = useAuth();
  const queryClient = useQueryClient();

  const [connectOpen, setConnectOpen] = useState(false);
  const [metaConnecting, setMetaConnecting] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const { data: bizRow, isLoading } = useQuery({
    queryKey: ["businessWhatsApp", businessId],
    queryFn: async (): Promise<BusinessWhatsAppRow | null> => {
      if (!businessId) return null;
      const { data, error } = await supabase
        .from("businesses")
        .select(
          "business_id, whatsapp_provider, whatsapp_number, whatsapp_status, meta_phone_number_id, meta_connected_at"
        )
        .eq("business_id", businessId)
        .maybeSingle();
      if (error) throw error;
      return (data as BusinessWhatsAppRow | null) ?? null;
    },
    enabled: !!user && !!businessId,
    staleTime: 15000,
  });

  // Credentials are stored and the webhook is wired, but only Meta saying
  // CONNECTED means messages actually arrive. See lib/whatsappConnection.
  const metaCredentialsStored = hasMetaWhatsAppCredentials(bizRow);
  const metaState = metaWhatsAppState(bizRow);
  const metaConnected = metaState === "connected";
  const metaPendingRegistration = metaState === "pending_registration";
  const historicalNonMetaConnection = hasLegacyWhatsAppConnection(bizRow);

  const metaAvailable = metaWhatsAppEnabled() && embeddedSignupConfigured();
  const busy = metaConnecting || disconnecting;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["businessWhatsApp", businessId] });

  const handleMetaConnect = async () => {
    if (!bizRow?.business_id) {
      toast.error("Couldn't find your business. Please refresh.");
      return;
    }
    setMetaConnecting(true);
    try {
      const { code, codeReceivedAt, phoneNumberId, wabaId } =
        await launchWhatsAppEmbeddedSignup();
      const { data, error } = await supabase.functions.invoke("meta-whatsapp-onboard", {
        body: {
          code,
          phone_number_id: phoneNumberId,
          waba_id: wabaId,
          // Measured client-side so it is free of clock skew; the function logs
          // it to tell an expired code apart from a real Meta config problem.
          code_age_ms: Date.now() - codeReceivedAt,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const where = data?.display_phone_number
        ? ` on ${data.display_phone_number}`
        : "";
      if (data?.live === false) {
        // Credentials are saved, but Meta has not made the number live, so
        // promising a live AI here would recreate the exact silent failure
        // this is meant to prevent.
        toast.warning(
          data?.live_message ??
            `WhatsApp is connected${where}, but Meta hasn't activated the number for messaging yet.`
        );
      } else {
        toast.success(`WhatsApp connected${where}. Your AI is live.`);
      }
      setConnectOpen(false);
      await refresh();
    } catch (err: any) {
      console.error("Meta WhatsApp connect failed:", err);
      // Embedded-signup cancellation is a plain Error; edge failures unwrap via readEdgeError.
      const msg = err?.message?.includes("cancelled")
        ? err.message
        : await readEdgeError(err, "Couldn't connect WhatsApp. Please try again.");
      toast.error(msg);
    } finally {
      setMetaConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!bizRow?.business_id) return;
    setDisconnecting(true);
    try {
      const { data, error } = await supabase.functions.invoke("whatsapp-disconnect", {
        body: { business_id: bizRow.business_id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("WhatsApp disconnected.");
      await refresh();
      setDisconnectOpen(false);
    } catch (err: any) {
      console.error("Disconnect failed:", err);
      toast.error(await readEdgeError(err, "Couldn't disconnect."));
    } finally {
      setDisconnecting(false);
    }
  };

  const cardDescription = "Connect your WhatsApp account to enable Workflows later.";

  // render
  return (
    <>
      <div data-testid="whatsapp-connection-card" className={`flex items-center justify-between gap-4 p-4 border rounded-xl bg-card shadow-sm ${className ?? ""}`}>
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#25D366]/10">
            <WhatsAppIcon className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm">WhatsApp</h3>
              {metaConnected && (
                <Badge
                  variant="default"
                  className="bg-green-600 hover:bg-green-600 text-white border-transparent"
                >
                  Connected
                </Badge>
              )}
              {metaPendingRegistration && (
                <Badge
                  variant="outline"
                  className="border-amber-300 bg-amber-50 text-amber-700"
                  data-testid="whatsapp-pending-registration"
                >
                  <Clock className="mr-1 h-3 w-3" />
                  Finishing setup
                </Badge>
              )}
              {historicalNonMetaConnection && (
                <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
                  <AlertTriangle className="mr-1 h-3 w-3" />
                  Update required
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{cardDescription}</p>
          </div>
        </div>
        <div className="shrink-0">
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          ) : (
            <div className="flex items-center gap-3">
              {metaPendingRegistration && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setConnectOpen(true)}
                >
                  Finish setup
                </Button>
              )}
              <Switch
                checked={metaConnected}
                disabled={busy}
                onCheckedChange={(checked) => {
                  if (!checked && metaCredentialsStored) setDisconnectOpen(true);
                  else setConnectOpen(true);
                }}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-muted">
                    <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {metaConnected && (
                    <>
                      <DropdownMenuLabel className="font-normal text-muted-foreground">
                        {bizRow?.whatsapp_number
                          ? `Connected as ${bizRow.whatsapp_number}`
                          : "Connected"}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {metaCredentialsStored ? (
                    <DropdownMenuItem onClick={() => setDisconnectOpen(true)} className="text-destructive">
                      Disconnect
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => setConnectOpen(true)} disabled={busy}>
                      Connect with Meta
                    </DropdownMenuItem>
                  )}
                  {historicalNonMetaConnection && (
                    <DropdownMenuItem onClick={() => setDisconnectOpen(true)} className="text-destructive">
                      Disconnect current connection
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* All new WhatsApp connections use Meta Embedded Signup. */}
      <Dialog open={connectOpen} onOpenChange={(open) => !busy && setConnectOpen(open)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Connect WhatsApp with Meta</DialogTitle>
            <DialogDescription>
              Link your WhatsApp Business number so Dibs can reply to customers automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {metaPendingRegistration && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Your number isn't receiving messages yet</AlertTitle>
                <AlertDescription className="text-sm">
                  Meta hasn't activated this number, so nothing reaches your AI. Run the
                  connection again and make sure you finish the step on your phone — that
                  is what links your WhatsApp Business app.
                </AlertDescription>
              </Alert>
            )}

            <Alert>
              <Smartphone className="h-4 w-4" />
              <AlertTitle>Connect directly with Meta</AlertTitle>
              <AlertDescription className="text-sm">
                WhatsApp <strong>Coexistence</strong> lets you keep using the WhatsApp Business
                app on your phone while Dibs' AI handles customer enquiries in parallel.
                Choose <strong>connect your existing WhatsApp Business app</strong> when Meta
                offers it, and complete the confirmation on your phone — skipping that step
                is what leaves a number unable to receive messages.
              </AlertDescription>
            </Alert>

            {metaAvailable ? (
              <Button className="w-full" onClick={handleMetaConnect} disabled={busy}>
                {metaConnecting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="mr-2 h-4 w-4" />
                )}
                Continue with Meta
              </Button>
            ) : (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Meta connection temporarily unavailable</AlertTitle>
                <AlertDescription>
                  Please contact Dibs support so we can restore the Meta connection flow for your account.
                </AlertDescription>
              </Alert>
            )}

            <p className="text-center text-xs text-muted-foreground">
              Dibs connects WhatsApp directly through the Meta Cloud API.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)} disabled={busy}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect confirmation */}
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect WhatsApp?</AlertDialogTitle>
            <AlertDialogDescription>
              Customers will no longer get automatic AI replies on WhatsApp. Your conversation
              history is preserved, and you can reconnect anytime. This does not affect your use of
              the WhatsApp Business app on your phone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={disconnecting}
              onClick={(e) => {
                e.preventDefault();
                handleDisconnect();
              }}
            >
              {disconnecting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WhatsAppConnectionCard;
